import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { FadeUp, InviewMaskLine } from "./Reveal";
import CarCard, { carName } from "./CarCard";
import SearchSelect from "./SearchSelect";
import { sortedCountries, countryName } from "../data/countries";
import { WHATSAPP_PRIMARY, waLink } from "../siteConfig";
import { SAMPLE_REPORT_PDF, SAMPLE_REPORT_VIDEO } from "../siteConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSPECTION_PHOTOS = ["/images/inspection-1.webp", "/images/inspection-2.webp", "/images/inspection-3.webp"];

// Turnkey service — full version on Home, short version on For Private Clients.
// The destination country is used only when the visitor explicitly selects it
// (remembered locally); otherwise a neutral heading is shown. Never inferred
// from IP or browser location.
export function TurnkeySection({ compact = false, number = "05", ctaMode = "link", onCta }) {
  const { t, lang } = useLang();
  const n = t("nx.turnkey");
  const [dest, setDest] = useState(() => {
    try { return localStorage.getItem("veyrone-destination") || ""; } catch { return ""; }
  });
  const country = dest ? countryName(dest, lang) : "";
  const eyebrow = country
    ? `${n.eyebrowBase} ${n.glue} ${country.toUpperCase()}`
    : `${n.eyebrowBase} ${n.eyebrowNoCountry}`;
  const points = compact ? n.points.slice(0, 3) : n.points;
  const pickDest = (v) => {
    setDest(v);
    try { localStorage.setItem("veyrone-destination", v); } catch { /* private mode */ }
  };
  const cta = ctaMode === "scroll" ? (
    <button type="button" onClick={onCta} className="btn btn-solid justify-between w-fit" data-testid="turnkey-cta">
      <span>{n.cta}</span><span aria-hidden="true">→</span>
    </button>
  ) : (
    <Link to="/contact" className="btn btn-solid justify-between w-fit" data-testid="turnkey-cta">
      <span>{n.cta}</span><span aria-hidden="true">→</span>
    </Link>
  );
  return (
    <section className="hairline-t bg-[#070809] px-5 md:px-10 py-24 md:py-32" data-testid={compact ? "turnkey-compact" : "turnkey-section"}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <FadeUp><div className="eyebrow mb-8">{number} — {eyebrow}</div></FadeUp>
          <h2 className="font-display text-[32px] sm:text-[46px] lg:text-[52px] text-[#F3F3F0] mb-8">
            {n.title.split("\n").map((l, i) => <InviewMaskLine key={i} delay={i * 0.12}>{l}</InviewMaskLine>)}
          </h2>
          <FadeUp delay={0.1}><p className="text-[15px] leading-relaxed text-[#999995] max-w-lg mb-6">{n.body}</p></FadeUp>
          <FadeUp delay={0.14} className="max-w-xs mb-10" data-testid="turnkey-destination-picker">
            <div className="field-label">{n.selectLabel}</div>
            <SearchSelect
              testid="turnkey-destination"
              value={dest}
              onChange={pickDest}
              options={sortedCountries(lang).map((c) => ({ value: c.code, label: c.name }))}
              placeholder={n.selectPh}
              emptyLabel={t("sell.noMatches")}
            />
          </FadeUp>
          <FadeUp delay={0.18}>{cta}</FadeUp>
        </div>
        <div className="border-t border-l border-white/10 self-start">
          {points.map((p, i) => (
            <FadeUp key={p.t} delay={i * 0.06} className="border-b border-r border-white/10 p-6 md:p-8">
              <div className="mono text-[11px] text-[#555654] mb-5">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="mono text-[12px] tracking-[0.2em] text-[#F3F3F0] mb-3">{p.t}</h3>
              <p className="text-[13px] leading-relaxed text-[#8A8A86]">{p.d}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// Inspection you can see — sample report, gallery, video slot, sample PDF slot.
export function InspectionSection({ number = "06" }) {
  const { t } = useLang();
  const n = t("nx.inspection");
  return (
    <section className="hairline-t px-5 md:px-10 py-24 md:py-32" data-testid="inspection-section">
      <FadeUp><div className="eyebrow mb-8">{number} — {n.eyebrow}</div></FadeUp>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <h2 className="font-display text-[32px] sm:text-[46px] text-[#F3F3F0] mb-8">
            {n.title.split("\n").map((l, i) => <InviewMaskLine key={i} delay={i * 0.12}>{l}</InviewMaskLine>)}
          </h2>
          <FadeUp delay={0.1}><p className="text-[15px] leading-relaxed text-[#999995] max-w-lg mb-10">{n.body}</p></FadeUp>
          <div className="border-t border-white/10 mb-10">
            {n.reportPoints.map((p, i) => (
              <FadeUp key={p} delay={i * 0.04}>
                <div className="flex items-center justify-between border-b border-white/10 py-3.5">
                  <span className="mono text-[11px] tracking-[0.16em] text-[#F3F3F0]">{p}</span>
                  <span className="mono text-[10px] text-[#555654]">✓</span>
                </div>
              </FadeUp>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {SAMPLE_REPORT_PDF && (
              <a href={SAMPLE_REPORT_PDF} target="_blank" rel="noopener noreferrer" className="btn btn-solid justify-between" data-testid="sample-report-btn">
                <span>{n.pdfBtn}</span><span aria-hidden="true">↓</span>
              </a>
            )}
            <Link to="/cars" className="btn justify-between" data-testid="inspection-cars-cta">
              <span>{n.carsCta}</span><span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
        <div>
          <div className="grid grid-cols-2 gap-3" data-testid="inspection-gallery">
            {INSPECTION_PHOTOS.map((src, i) => (
              <img key={src} src={src} alt={`${n.eyebrow} — inspection photo ${i + 1}`}
                width="800" height="600" loading="lazy"
                className={`w-full aspect-[4/3] object-cover border border-white/10 ${i === 0 ? "col-span-2 aspect-[8/5]" : ""}`} />
            ))}
          </div>
          {SAMPLE_REPORT_VIDEO && (
            <div className="mt-4 border border-white/10" data-testid="inspection-video">
              <video src={SAMPLE_REPORT_VIDEO} controls preload="none" className="w-full aspect-video" />
            </div>
          )}
          <div className="mono text-[10px] tracking-[0.2em] text-[#666663] mt-3 uppercase">{n.galleryCaption}</div>
        </div>
      </div>
    </section>
  );
}

// Results — completed operations + client reviews. Hidden until content exists.
export function ResultsSection({ number = "08" }) {
  const { t } = useLang();
  const n = t("nx.results");
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetch(`${API}/results`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setItems).catch(() => setItems([]));
  }, []);
  if (!items.length) return null;
  return (
    <section className="hairline-t px-5 md:px-10 py-24 md:py-32" data-testid="results-section">
      <FadeUp><div className="eyebrow mb-8">{number} — {n.eyebrow}</div></FadeUp>
      <h2 className="font-display text-[34px] sm:text-[48px] text-[#F3F3F0] mb-14"><InviewMaskLine>{n.title}</InviewMaskLine></h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {items.map((r) => (
          <div key={r.id} className="border border-white/10 bg-[#0A0B0C] p-7 md:p-8 flex flex-col" data-testid={`result-card-${r.id}`}>
            <div className="mono text-[9px] tracking-[0.2em] text-[#D1C7BD] uppercase mb-5">{r.type === "review" ? n.typeReview : n.typeOperation}</div>
            <h3 className="mono text-[12px] tracking-[0.16em] text-[#F3F3F0] uppercase">{r.title}</h3>
            {r.body && <p className="text-[13px] leading-relaxed text-[#8A8A86] mt-4">{r.body}</p>}
            {(r.name || r.date_label) && (
              <div className="mono text-[10px] text-[#555654] uppercase tracking-[0.16em] mt-auto pt-6">{r.name} {r.date_label && `· ${r.date_label}`}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// Costs and timelines — fee, payment steps, delivery timeline (placeholders shown).
export function CostsSection({ number = "07" }) {
  const { t } = useLang();
  const n = t("nx.costs");
  return (
    <section className="hairline-t px-5 md:px-10 py-24 md:py-32" data-testid="costs-section">
      <FadeUp><div className="eyebrow mb-8">{number} — {n.eyebrow}</div></FadeUp>
      <h2 className="font-display text-[32px] sm:text-[46px] text-[#F3F3F0] mb-12">
        {n.title.split("\n").map((l, i) => <InviewMaskLine key={i} delay={i * 0.12}>{l}</InviewMaskLine>)}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 border-t border-l border-white/10">
        {[
          { t: n.includesTitle, d: n.includes.join("\n"), items: n.includes },
          { t: n.feeTitle, d: n.fee },
          { t: n.paymentTitle, d: n.payment },
          { t: n.timelineTitle, d: n.timeline },
        ].map((b, i) => (
          <FadeUp key={b.t} delay={i * 0.05} className={`border-b border-r border-white/10 p-7 md:p-9 ${i === 0 ? "md:col-span-2 xl:col-span-1" : ""}`}>
            <h3 className="mono text-[12px] tracking-[0.2em] text-[#F3F3F0] mb-5">{b.t}</h3>
            {"items" in b ? (
              <ul className="space-y-2.5">
                {b.items.map((x) => <li key={x} className="text-[13px] leading-relaxed text-[#8A8A86] flex gap-2.5"><span className="text-[#D1C7BD]">—</span>{x}</li>)}
              </ul>
            ) : (
              <p className="text-[13px] leading-relaxed text-[#8A8A86] whitespace-pre-line">{b.d}</p>
            )}
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

// Home — 3 latest available cars from real stock; friendly empty state when no stock yet.
export function StockOpportunities({ number = "03" }) {
  const { t } = useLang();
  const n = t("nx.homeOpp");
  const [cars, setCars] = useState(null);
  useEffect(() => {
    fetch(`${API}/cars`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setCars).catch(() => setCars([]));
  }, []);
  const latest = (cars || []).filter((c) => c.status === "available").slice(0, 3);
  return (
    <section className="hairline-t px-5 md:px-10 py-24 md:py-32" data-testid="opportunities-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
        <div>
          <FadeUp><div className="eyebrow mb-8">{number} — {t("opp.eyebrow")}</div></FadeUp>
          <h2 className="font-display text-[34px] sm:text-[48px] text-[#F3F3F0]">
            <InviewMaskLine>{t("opp.title")}</InviewMaskLine>
          </h2>
        </div>
        <FadeUp delay={0.15} className="max-w-sm">
          <p className="text-[13px] leading-relaxed text-[#8A8A86]">{t("opp.sub")}</p>
        </FadeUp>
      </div>
      {cars !== null && latest.length === 0 && (
        <div className="border border-white/10 bg-[#0A0B0C] p-10 md:p-14 text-center" data-testid="opportunities-empty">
          <p className="text-[14px] text-[#999995] max-w-md mx-auto mb-8">{n.empty}</p>
          <Link to="/contact" className="btn btn-solid justify-between w-fit mx-auto" data-testid="opportunities-cta">
            <span>{n.cta}</span><span aria-hidden="true">→</span>
          </Link>
        </div>
      )}
      {latest.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {latest.map((car, i) => <CarCard key={car.id} car={car} index={i} />)}
        </div>
      )}
    </section>
  );
}
